// Requesting a song: the Spotify search + Trending feed (with the saved
// list and the session caches), the picked track, the bid box's state, the
// fee maths, and the debounced PaymentIntent. Split out of
// pages/RequestPage.tsx; SongSearchFeed and SongCheckout render it.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { paymentsApi } from "../services/payments";
import { spotifyApi } from "../services/spotify";
import type { Track } from "../types/spotify";
import type { SpotlightChoice } from "../types/payments";
import { roundPriceStep } from "../utils/roundPriceStep";
import { maxPriceFor } from "../utils/payinConfig";
import { calculateFeeBreakdown } from "../utils/feeCalculator";
import { dedupeTracks } from "../utils/dedupeTracks";
import { STRIPE_PK, stripePromise, customerCopyForPayError } from "../lib/fanCheckout";
import { t as tr } from "../i18n";

type FeedTab = "for_you" | "trending" | "saved";

// Matches both full share URLs (incl. /intl-<lang>/ variants) and spotify: URIs.
const SPOTIFY_TRACK_RE =
  /(?:open\.spotify\.com\/(?:intl-[a-z]{2}\/)?track\/|spotify:track:)([a-zA-Z0-9]{22})/;
export const parseSpotifyTrackId = (s: string): string | null => {
  const m = (s || "").match(SPOTIFY_TRACK_RE);
  return m ? m[1] : null;
};

type Tier = "standard" | "fast";
type PricingMode = "tile" | "custom";

/** === Быстрый фид === */
const FEED_SIZE = 45;
const CACHE_TRENDING = "rq_cache_trending_v1";
const CACHE_FORYOU = "rq_cache_foryou_v1"; // хранит последнюю "for_you"

const RANDOM_QUERIES = [
  "love", "you", "the", "night", "dance", "party", "summer", "baby",
  "rock", "pop", "rap", "house", "moon", "blue", "time", "life", "heart", "fire",
];
const pickRand = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

type Args = {
  dj: any;
  viewDj: any;
  isProfilePreview: boolean;
  minPrice: number;
  isFree: boolean;
  djCur: string | undefined;
  comment: string;
  clientNickname: string;
  clientInstagram: string;
};

export function useSongRequest({ dj, viewDj, isProfilePreview, minPrice, isFree, djCur, comment, clientNickname, clientInstagram }: Args) {
  const [activeTab] = useState<FeedTab>("trending");

  /** Saved */
  const [savedIds, setSavedIds] = useState<string[]>(
    () => JSON.parse(localStorage.getItem("rq_saved_tracks") || "[]")
  );

  /** Фиды/поиск */
  const [feed, setFeed] = useState<Track[]>([]);
  // Trending is this DJ's country chart (1.5.1 #14) — cached per DJ, and the
  // chart's country remembered beside it for the "Trending · France" label.
  const djSlug: string | undefined = dj?.slug;
  const trendKey = djSlug ? `${CACHE_TRENDING}:${djSlug}` : CACHE_TRENDING;
  const [feedCountry, setFeedCountry] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(`${trendKey}:country`);
    } catch {
      return null;
    }
  });
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<Track[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  /** Платежи */
  const [baseAmount, setBaseAmount] = useState<string>("");
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [tier, setTier] = useState<Tier>("standard");
  const [bidInfoOpen, setBidInfoOpen] = useState(false);
  const [bidPlaceholder, setBidPlaceholder] = useState<string>("");

  const [showPayInline, setShowPayInline] = useState(false);
  const [payInlineLoading, setPayInlineLoading] = useState(false);
  const [piClientSecret, setPiClientSecret] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const [pricingMode, setPricingMode] = useState<PricingMode>("tile");
  const [bidFocused, setBidFocused] = useState(false);
  const [bidTouched, setBidTouched] = useState(false);

  const resultsRef = useRef<HTMLUListElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  /** True while a focus() we call ourselves is landing: the search box's
      on-focus "rise to the top" is for the fan's own tap only — a
      programmatic focus must leave the page pinned where it is. */
  const riseSuppressedRef = useRef(false);
  const feedRef = useRef<HTMLDivElement | null>(null);
  // A tap that dismisses the search dropdown can land on a Trending row
  // peeking below it — swallow that tap's row-select.
  const suppressFeedTapRef = useRef(false);
  const [justSelected, setJustSelected] = useState(false);
  const [searchParams] = useSearchParams();

  const piDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Request Spotlight: with it on, the paid checkout shows the box ("Show my
      name on DJ Lucky's screen") and the fan may hide it. The request says the
      box was shown and what was chosen; "Hide my name" can be tapped after the
      intent exists, so the confirm carries the final choice too and a change
      never re-mints the intent. Spotlight off = today's payload, unchanged. */
  const spotlightOn = !isProfilePreview && !isFree && dj?.spotlightEnabled === true;
  const [spotlightNameHidden, setSpotlightNameHidden] = useState(false);
  const spotlightChoice: SpotlightChoice | null = spotlightOn ? { spotlightAware: true, spotlightNameHidden } : null;
  const spotlightRef = useRef<SpotlightChoice | null>(spotlightChoice);
  spotlightRef.current = spotlightChoice;

  /** Кэши в памяти */
  const searchCache = useRef<Map<string, Track[]>>(new Map());
  const feedCache = useRef<{ for_you?: Track[]; trending?: Track[]; saved?: Track[] }>({});

  /** Close the bid info popover on outside tap (mousedown + touchstart for iOS). */
  useEffect(() => {
    if (!bidInfoOpen) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest(".rq-tier-info-btn") || target.closest(".rq-tier-info-popover")) return;
      setBidInfoOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [bidInfoOpen]);

  /** === Поиск: отмена предыдущего + кэш === */
  useEffect(() => {
    if (searchQuery.length < 2 || selectedTrack?.name === searchQuery) {
      setResults([]);
      setSearchError(null);
      return;
    }
    const key = searchQuery.trim().toLowerCase();
    const cached = searchCache.current.get(key);
    if (cached) {
      setResults(cached);
      return;
    }

    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const data = await spotifyApi.search(key, ctrl.signal);
        const deduped = dedupeTracks(data.tracks || []);
        searchCache.current.set(key, deduped);
        setResults(deduped);
        setSearchError(null);
      } catch (e: any) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          console.error("Spotify search error", e);
          // Don't leave the fan staring at a dead box — say it's us, not them.
          setResults([]);
          setSearchError(tr("useSongRequest.songSearchIsTemporarily"));
        }
      }
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [searchQuery, selectedTrack]);

  /** Закрытие выпадашки по клику вне */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      suppressFeedTapRef.current = false;
      if (justSelected) {
        setJustSelected(false);
        return;
      }
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setResults([]);
        if (feedRef.current?.contains(e.target as Node)) {
          suppressFeedTapRef.current = true;
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [justSelected]);

  /** Фокус на поле поиска при входе/очистке */
  useEffect(() => {
    // Not in the DJ's own profile Preview — auto-opening the keyboard there
    // scrolls the embedded page for no reason. Real fans keep the autofocus.
    // preventScroll keeps the late focus from un-pinning the page.
    if (isProfilePreview) return;
    const t = setTimeout(() => {
      const el = searchInputRef.current;
      if (!el) return;
      // focus() and click() both dispatch synchronously, so the flag covers
      // exactly these two and never leaks onto the fan's own tap.
      riseSuppressedRef.current = true;
      el.focus({ preventScroll: true });
      el.click();
      riseSuppressedRef.current = false;
      window.scrollTo(0, 0);
    }, 200);
    return () => clearTimeout(t);
  }, [isProfilePreview]);

  /** Pin the page to the top when a song is picked. Selecting a track
      collapses the search + Trending feed, and the browser's leftover scroll
      offset (from a scrolled feed) would otherwise cut the profile pic off.
      The bid box sits below the profile header — fans scroll to it if needed;
      nothing auto-scrolls. */
  useEffect(() => {
    if (isProfilePreview || !selectedTrack) return;
    window.scrollTo(0, 0);
  }, [isProfilePreview, selectedTrack]);

  /** Keyboard dismisses the moment the fan scrolls (founder decision) — the
      Spotify/YouTube convention consumers expect. touchmove + wheel fire
      only on USER-initiated scrolling, so our own programmatic pins never
      close it. The search box, the message box and the bid box (founder
      2026-10-26: "when you scroll anywhere, the keyboard disappears";
      leaving the bid box commits the bid as a tap away does). */
  useEffect(() => {
    const dismissKeyboardOnScroll = () => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el === searchInputRef.current || el.matches(".rq-comment, .rq-bid-input"))) el.blur();
    };
    window.addEventListener("touchmove", dismissKeyboardOnScroll, { passive: true });
    window.addEventListener("wheel", dismissKeyboardOnScroll, { passive: true });
    return () => {
      window.removeEventListener("touchmove", dismissKeyboardOnScroll);
      window.removeEventListener("wheel", dismissKeyboardOnScroll);
    };
  }, []);

  /** Параметр кастомной цены в URL */
  useEffect(() => {
    const na = searchParams.get("new_amount");
    if (na && !isNaN(Number(na))) {
      setBaseAmount(String(Number(na)));
      setPricingMode("custom");
    }
  }, [searchParams]);

  /** Shown under the search input when a non-Spotify link is pasted. */
  const [linkHint, setLinkHint] = useState<string | null>(null);
  const linkHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashLinkHint = (msg: string) => {
    setLinkHint(msg);
    if (linkHintTimerRef.current) clearTimeout(linkHintTimerRef.current);
    linkHintTimerRef.current = setTimeout(() => setLinkHint(null), 6000);
  };

  /** Saved toggle */
  const toggleSaved = (id: string) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("rq_saved_tracks", JSON.stringify(next));
      feedCache.current.saved = undefined;
      return next;
    });
  };

  /** Fetch a Spotify track by ID and jump straight to the selected-track state. */
  const resolveSpotifyLink = async (trackId: string): Promise<void> => {
    try {
      const data = await spotifyApi.byIds([trackId]);
      const track: Track | undefined = (data?.tracks || [])[0];
      if (!track) return;
      setSelectedTrack(track);
      setSearchQuery(track.name);
      setResults([]);
      setBaseAmount(String(minPrice));
      setPricingMode("custom");
    } catch {
      /* silent — user can fall back to typing a search */
    }
  };

  /** === Быстрая отдача из sessionStorage === */
  const instantFromSession = (key: string): Track[] | null => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const arr = JSON.parse(raw) as Track[];
      return Array.isArray(arr) && arr.length ? arr.slice(0, FEED_SIZE) : null;
    } catch {
      return null;
    }
  };

  const saveToSession = (key: string, value: Track[]) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value.slice(0, FEED_SIZE)));
    } catch { }
  };

  /** === Запасной план === */
  const fetchRandomTracks = async (n = FEED_SIZE): Promise<Track[]> => {
    const qs = Array.from({ length: 4 }, () => pickRand(RANDOM_QUERIES));
    try {
      const results = await Promise.all(
        qs.map((q) => spotifyApi.search(q))
      );
      const all: Track[] = [];
      for (const r of results) all.push(...(r.tracks || []));
      return dedupeTracks(all).slice(0, n);
    } catch {
      return [];
    }
  };

  /** === Основная загрузка фида с SWR === */
  const loadFeed = async (tab: FeedTab) => {
    const mem = feedCache.current[tab];
    if (mem && mem.length) {
      setFeed(mem.slice(0, FEED_SIZE));
      setLoadingFeed(false);
    } else {
      if (tab === "trending") {
        const ss = instantFromSession(trendKey);
        if (ss) { setFeed(ss); setLoadingFeed(false); } else setLoadingFeed(true);
      } else if (tab === "for_you") {
        const ss = instantFromSession(CACHE_FORYOU) || instantFromSession(CACHE_TRENDING);
        if (ss) { setFeed(ss); setLoadingFeed(false); } else setLoadingFeed(true);
      } else {
        setLoadingFeed(true);
      }
    }

    try {
      if (tab === "saved") {
        if (!savedIds.length) { setFeed([]); feedCache.current.saved = []; setLoadingFeed(false); return; }
        try {
          const data = await spotifyApi.byIds(savedIds);
          const next = dedupeTracks(data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.saved = next;
        } catch { setFeed([]); feedCache.current.saved = []; }
        finally { setLoadingFeed(false); }
        return;
      }

      if (tab === "trending") {
        try {
          const data = await spotifyApi.trending(FEED_SIZE, { dj: djSlug });
          const next = dedupeTracks(data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.trending = next; saveToSession(trendKey, next);
          setFeedCountry(data.country || null);
          try {
            if (data.country) sessionStorage.setItem(`${trendKey}:country`, data.country);
          } catch {}
        } catch {
          const fallback = await fetchRandomTracks(FEED_SIZE);
          setFeed(fallback); feedCache.current.trending = fallback;
        } finally { setLoadingFeed(false); }
        return;
      }

      const ordered = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]") as string[];
      if (ordered.length) {
        try {
          const seed = ordered.slice(-5);
          const data = await spotifyApi.recommend(seed, FEED_SIZE);
          const next = dedupeTracks(data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
        } catch {
          try {
            const data = await spotifyApi.trending(FEED_SIZE);
            const next = dedupeTracks(data.tracks || []).slice(0, FEED_SIZE);
            setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
          } catch {
            const fallback = await fetchRandomTracks(FEED_SIZE);
            setFeed(fallback); feedCache.current.for_you = fallback;
          }
        } finally { setLoadingFeed(false); }
      } else {
        try {
          const data = await spotifyApi.trending(FEED_SIZE, { rand: true });
          const next = dedupeTracks(data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
        } catch {
          const fallback = await fetchRandomTracks(FEED_SIZE);
          setFeed(fallback); feedCache.current.for_you = fallback;
        } finally { setLoadingFeed(false); }
      }
    } catch (e) {
      console.error("feed error", e);
      const fallback = await fetchRandomTracks(FEED_SIZE);
      setFeed(fallback); feedCache.current[tab] = fallback; setLoadingFeed(false);
    }
  };

  const savedIdsKey = JSON.stringify(savedIds);
  useEffect(() => {
    // Waits for the DJ: Trending is their country's chart.
    if (!selectedTrack && djSlug) loadFeed(activeTab);
    // loadFeed is recreated every render; the feed is keyed on the tab, the
    // selection, the saved list and the DJ only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTrack, savedIdsKey, djSlug]);

  // БАЗА = сколько "стоит запрос" у диджея (с учётом tier)
  const requestBase = (() => {
    if (isFree) return 0;
    const raw = Number(baseAmount || minPrice);
    // Fraud door: the bid is bounded above by the DJ's request
    // cap — the server refuses anything higher (/create-intent), so clamp
    // here too and the fan never sees a surprise error at pay time.
    const cap = maxPriceFor(djCur, viewDj?.capsLifted);
    const clamped = isNaN(raw) ? minPrice : Math.min(Math.max(minPrice, raw), cap);
    return roundPriceStep(clamped, djCur);
  })();

  // Customer pays: base + estimated Stripe fee, rounded up to nearest $0.50 or $1.
  // Platform's 15% is taken via Stripe application_fee at payment time.
  const feeBreakdown = isFree || requestBase <= 0
    ? { baseAmount: 0, customerTotal: 0, applicationFee: 0, djNetShare: 0, estimatedStripeFee: 0 }
    : calculateFeeBreakdown(requestBase, djCur);

  const totalDisplay = feeBreakdown.customerTotal;
  const fastThreshold = minPrice * 2;
  const isFastPassBid = !isFree && requestBase >= fastThreshold;

  useEffect(() => {
    if (isFree) return;
    if (requestBase >= fastThreshold && tier !== "fast") {
      setTier("fast");
    } else if (requestBase < fastThreshold && tier !== "standard") {
      setTier("standard");
    }
  }, [requestBase, fastThreshold, isFree, tier]);

  const rememberOrdered = (trackId: string) => {
    const arr = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]");
    const next = Array.from(new Set([...(arr as string[]), trackId])).slice(-20);
    localStorage.setItem("rq_ordered_tracks", JSON.stringify(next));
  };

  /** Автосоздание PaymentIntent */
  useEffect(() => {
    if (isProfilePreview) return;
    if (isFree) return;
    if (!dj?.slug) return;

    if (!selectedTrack) {
      setShowPayInline(false);
      setPayInlineLoading(false);
      setPiClientSecret(null);
      setCreatedOrderId(null);
      setShowCardForm(false);
      setPayError(null);
      return;
    }

    if (!stripePromise && STRIPE_PK) {
      console.warn("Stripe publishable key is missing on the frontend");
      return;
    }

    if (requestBase < minPrice) {
      setShowPayInline(false);
      setPayInlineLoading(false);
      setPiClientSecret(null);
      setCreatedOrderId(null);
      setShowCardForm(false);
      setPayError(null);
      return;
    }

    setShowPayInline(false);
    setPayInlineLoading(true);
    setPiClientSecret(null);
    setShowCardForm(false);
    setPayError(null);

    const reqId =
      (window.crypto?.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "");
    (window as any).__rq_last_req = reqId;

    if (piDebounceRef.current) clearTimeout(piDebounceRef.current);
    piDebounceRef.current = setTimeout(async () => {
      try {
        const payload = {
          djSlug: dj.slug,
          searchQuery: selectedTrack.name,
          trackId: selectedTrack.id,
          amount: totalDisplay,
          requestAmount: requestBase,
          comment,
          trackImage: selectedTrack.image,
          trackArtists: selectedTrack.artists.map((a) => a.name),
          trackAlbum: selectedTrack.album,
          isPriority: false,
          isFast: tier === "fast",
          tier,
          orderId: createdOrderId || undefined,
          clientNickname: clientNickname || undefined,
          clientInstagram: clientInstagram || undefined,
          ...(spotlightRef.current ?? {}),
        };

        const data = await paymentsApi.createRequestIntent(payload);

        if ((window as any).__rq_last_req !== reqId) return;

        setPiClientSecret(data.clientSecret);
        setCreatedOrderId(data.orderId);
        setShowPayInline(true);
        setPayInlineLoading(false);
        setShowCardForm(false);
        setPayError(null);

        sessionStorage.setItem("lastDjSlug", dj.slug);

        const arr = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]");
        const next = Array.from(new Set([...(arr as string[]), selectedTrack.id])).slice(-20);
        localStorage.setItem("rq_ordered_tracks", JSON.stringify(next));
      } catch (e: any) {
        console.error(e);
        // Render situation-aware copy based on the server's `code`
        // discriminator instead of always showing the raw msg.
        const msg = customerCopyForPayError(e, dj?.name || "");
        setPayInlineLoading(false);
        setPayError(msg);
      }
    }, 350);

    return () => {
      if (piDebounceRef.current) clearTimeout(piDebounceRef.current);
    };
    // Keyed on what changes the price or the track. Nickname, comment and
    // Instagram ride along at pay time (SongCheckout sends them with the
    // confirm, 2026-10-10); re-minting an intent per keystroke would hammer
    // Stripe and flicker the pay sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dj?.slug,
    selectedTrack?.id,
    selectedTrack?.name,
    requestBase,
    totalDisplay,
    tier,
    pricingMode,
    isFree,
    minPrice,
    stripePromise,
    isProfilePreview,
  ]);

  // One reset for leaving the song checkout (selected-track state) — wired
  // to the top-left back arrow (founder: matching arrows on the song
  // checkout and the setlist payment sheet). Back always lands at the TOP
  // (founder decision) — a checkout's leftover scroll offset otherwise
  // dumps the fan mid-page.
  const clearSelectedSong = () => {
    setSelectedTrack(null);
    setSearchQuery("");
    setShowPayInline(false);
    setPiClientSecret(null);
    setCreatedOrderId(null);
    setShowCardForm(false);
    setPayError(null);
    setBidInfoOpen(false);
    window.scrollTo(0, 0);
  };

  return {
    savedIds,
    feed,
    feedCountry,
    loadingFeed,
    searchQuery,
    setSearchQuery,
    results,
    setResults,
    searchError,
    selectedTrack,
    setSelectedTrack,
    baseAmount,
    setBaseAmount,
    createdOrderId,
    tier,
    bidInfoOpen,
    setBidInfoOpen,
    bidPlaceholder,
    setBidPlaceholder,
    showPayInline,
    setShowPayInline,
    payInlineLoading,
    piClientSecret,
    setPiClientSecret,
    showCardForm,
    setShowCardForm,
    processing,
    setProcessing,
    payError,
    setPayError,
    setPricingMode,
    bidFocused,
    setBidFocused,
    bidTouched,
    setBidTouched,
    resultsRef,
    searchInputRef,
    riseSuppressedRef,
    feedRef,
    suppressFeedTapRef,
    setJustSelected,
    linkHint,
    setLinkHint,
    flashLinkHint,
    toggleSaved,
    resolveSpotifyLink,
    requestBase,
    totalDisplay,
    isFastPassBid,
    rememberOrdered,
    clearSelectedSong,
    spotlightOn,
    spotlightNameHidden,
    setSpotlightNameHidden,
    spotlightChoice,
  };
}

export type SongRequest = ReturnType<typeof useSongRequest>;
